/**
 * The Health section of an animal's Records: holds first (they decide whether it can be sold), then
 * sickness, the bull exam, and the history by kind: treatments, vaccinations, sickness and checks,
 * procedures and lab work.
 */
import { useState } from "react";
import type { AnimalDetail, EventDTO, Page } from "../../../contract";
import { qs } from "../../../web/lib/api";
import { formatDate, useFormat } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { useCan, useMe } from "../../../web/lib/session";
import { openRecord } from "../../../web/forms/EventForm";
import { registry } from "../../../web/registry";
import { EventRow } from "../../../web/shell/Timeline";
import { DataList } from "../../../web/shell/describe";
import { Button, Card, Chip, Icon, Loading } from "../../../web/ui/kit";
import { HEALTH_TYPES } from "../contract";
import type { StockAnswer } from "../shared/dto";
import { BSE_CLASSES, SEVERITY, activeHolds, holdsFromDose, type HealthState, type TreatmentData } from "../shared/logic";
import { API, Block, HoldLine, qty, unitLabel } from "./common";

const GROUPS: Array<{ id: string; label: string; types: string[] }> = [
  { id: "treatments", label: "Treatments", types: [HEALTH_TYPES.treatment] },
  { id: "vaccinations", label: "Vaccinations", types: [HEALTH_TYPES.vaccination] },
  { id: "sickness", label: "Sickness and checks", types: [HEALTH_TYPES.sickness] },
  { id: "procedures", label: "Procedures", types: [HEALTH_TYPES.procedure] },
  { id: "lab", label: "Lab work", types: [HEALTH_TYPES.sample, HEALTH_TYPES.result] },
  { id: "bse", label: "Bull exams", types: [HEALTH_TYPES.bse] },
];

const today = (tz?: string) => {
  try { return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()); } catch { return new Date().toISOString().slice(0, 10); }
};

export const healthState = (a: AnimalDetail): HealthState | null => (a.facets.health as HealthState | undefined) ?? null;

export function AnimalHealth({ animal }: { animal: AnimalDetail }) {
  useLanguage();
  const { data: me } = useMe();
  const can = useCan();
  const day = today(me?.ranch?.timezone);
  const state = healthState(animal);
  const events = useApi<Page<EventDTO>>(`/events${qs({ subject: `animal:${animal.id}`, module: "health", limit: 150 })}`);
  const subject = { kind: "animal" as const, id: animal.id, role: "animal", label: animal.label };
  const holds = activeHolds(state, day);
  const items = events.data?.items ?? [];
  const byId = new Map(items.map((e) => [e.id, e]));
  return (
    <div className="health-section stack">
      {(holds.length > 0 || (state?.unresolved?.length ?? 0) > 0) && (
        <Block title={t("Withdrawal holds")} tone="warn" icon="shieldAlert">
          {holds.map((h) => <HoldLine key={`${h.kind}${h.event_id}`} hold={h} today={day} />)}
          {state?.unresolved?.map((u) => {
            const e = byId.get(u.event_id);
            return (
              <div key={u.event_id} className="health-hold is-unknown">
                <span className="health-hold-icon"><Icon name="help" size={20} /></span>
                <span className="health-hold-main">
                  <strong>{t("Withdrawal not recorded")}</strong>
                  <span className="muted">{[u.product || t("Unknown product"), t("given {{date}}", { date: formatDate(u.on) })].join(" · ")}</span>
                </span>
                {e && can("record") && <Button size="sm" onClick={() => openRecord({ initial: { type: e.type, subjects: e.subjects, occurred: { value: e.occurred_at ?? undefined, precision: e.occurred_precision }, data: e.data, job_id: e.job_id }, existing: e })}>{t("Add it")}</Button>}
              </div>
            );
          })}
        </Block>
      )}

      {state?.sickness && (
        <Block title={state.sickness.status === "improving" ? t("Getting better") : t("Sick")} tone="bad" icon="thermometer"
          action={can("record") ? (
            <span className="row-actions">
              <Button size="sm" onClick={() => openRecord({ initial: { type: HEALTH_TYPES.sickness, subjects: [subject], data: { condition: state.sickness!.condition } } })}>{t("Recheck")}</Button>
              <Button size="sm" variant="primary" onClick={() => openRecord({ initial: { type: HEALTH_TYPES.treatment, subjects: [subject], data: { reason: state.sickness!.condition } } })}>{t("Treat")}</Button>
            </span>
          ) : undefined}>
          <p>
            <strong>{state.sickness.condition ?? t("Sick")}</strong>
            {" · "}{t("since {{date}}", { date: formatDate(state.sickness.since) })}
            {state.sickness.severity ? ` · ${t(SEVERITY[state.sickness.severity as keyof typeof SEVERITY] ?? state.sickness.severity)}` : ""}
          </p>
          <p className="muted">{t("Last checked {{date}}", { date: formatDate(state.sickness.checked) })}{state.sickness.recheck_on ? ` · ${t("recheck {{date}}", { date: formatDate(state.sickness.recheck_on) })}` : ""}{state.sickness.pulled ? ` · ${t("in the sick pen")}` : ""}</p>
        </Block>
      )}

      {state?.bse && (
        <Block title={t("Breeding soundness")} icon="microscope" tone={state.bse.classification === "satisfactory" ? "good" : "warn"}>
          <p>
            <strong>{t(BSE_CLASSES[state.bse.classification])}</strong>{" · "}{formatDate(state.bse.on)}
            {state.bse.scrotal ? ` · ${qty(state.bse.scrotal)}` : ""}
            {state.bse.motility !== undefined ? ` · ${t("{{pct}}% motility", { pct: state.bse.motility })}` : ""}
            {state.bse.morphology !== undefined ? ` · ${t("{{pct}}% normal", { pct: state.bse.morphology })}` : ""}
          </p>
          {state.bse.recheck_on && <p className="muted">{t("Retest {{date}}", { date: formatDate(state.bse.recheck_on) })}</p>}
        </Block>
      )}

      {events.isLoading ? <Loading /> : GROUPS.map((g) => <HistoryGroup key={g.id} label={t(g.label)} events={items.filter((e) => g.types.includes(e.type))} />)}
      {!events.isLoading && !items.length && <p className="muted">{t("No health records yet.")}</p>}
    </div>
  );
}

function HistoryGroup({ label, events }: { label: string; events: EventDTO[] }) {
  const [all, setAll] = useState(false);
  if (!events.length) return null;
  const shown = all ? events : events.slice(0, 4);
  return (
    <div className="health-history">
      <h3 className="health-history-title">{label}<span className="count">{events.length}</span></h3>
      <ol className="timeline-list">{shown.map((e) => <EventRow key={e.id} event={e} showSubjects={false} />)}</ol>
      {events.length > 4 && !all && <Button variant="ghost" size="sm" onClick={() => setAll(true)}>{plural(events.length, "Show {{count}} record", "Show all {{count}}")}</Button>}
    </div>
  );
}

/** Detail for a treatment or vaccination: the holds it set, what it took from stock, then every field. */
export function DoseDetail({ event }: { event: EventDTO }) {
  useLanguage();
  const { data: me } = useMe();
  const fmt = useFormat(event.labels);
  const stock = useApi<StockAnswer>(`${API}/events/${event.id}/stock`);
  const day = today(me?.ranch?.timezone);
  const data = event.data as TreatmentData;
  const on = event.occurred_precision !== "unknown" && event.occurred_at ? event.occurred_at.slice(0, 10) : "";
  const holds = holdsFromDose({ id: event.id, on, data: { ...data, product_name: data.product_name || (data.product_id ? event.labels[data.product_id] : undefined) } });
  const plan = stock.data?.plan;
  return (
    <div className="stack">
      {(holds.length > 0 || data.meat_withdrawal_days !== undefined || data.product_id || data.product_name) && (
        <Block title={t("Withdrawal")} icon="shieldAlert" tone={holds.some((h) => h.until > day) ? "warn" : "good"}>
          {holds.map((h) => (
            <p key={h.kind}>
              <strong>{h.kind === "meat" ? t("Meat hold until {{date}}", { date: formatDate(h.until) }) : t("Milk hold until {{date}}", { date: formatDate(h.until) })}</strong>
              {" · "}{h.until > day ? <Chip tone="warn">{t("Active")}</Chip> : <Chip tone="good" icon="done">{t("Ended")}</Chip>}
            </p>
          ))}
          {!holds.length && <p>{data.meat_withdrawal_days === 0 ? t("No meat withdrawal") : <strong className="health-unknown">{t("Meat withdrawal not recorded")}</strong>}</p>}
          {data.withdrawal_source && <p className="muted">{data.withdrawal_source === "label" ? t("From the product label") : t("From the vet's instructions")}</p>}
        </Block>
      )}
      {stock.data && (
        <p className="health-stock">
          <Icon name="stock" size={16} />
          {stock.data.taken
            ? t("Took {{amount}} from {{product}}{{lot}}.", { amount: `${stock.data.taken.amount} ${unitLabel(stock.data.taken.unit)}`, product: plan?.item ?? "", lot: plan?.lot ? ` · ${plan.lot}` : "" })
            : plan && plan.use.amount === null
              ? plan.use.problem === "no_dose" ? t("Not taken from stock: no dose was recorded.") : t("Not taken from stock: the dose isn't in {{unit}}, how {{product}} is counted.", { unit: unitLabel(plan.unit), product: plan.item })
              : event.status !== "active" ? t("Given back to stock when this record changed.") : t("Not a product from supplies, so nothing was taken from stock.")}
        </p>
      )}
      <Card><DataList fields={registry.event(event.type)?.fields ?? {}} data={event.data} fmt={fmt} /></Card>
    </div>
  );
}
